import React, { useState, useEffect } from "react";
import { subscribe, getIngestStatus } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Bell, Mail, Check, Info } from "lucide-react";
import { toast } from "sonner";

const GAMES = [
  { id: "mega_millions", label: "Mega Millions", color: "#FFD700" },
  { id: "powerball", label: "Powerball", color: "#00F0FF" },
];

export const AlertsCard = () => {
  const [email, setEmail] = useState("");
  const [selected, setSelected] = useState(["mega_millions", "powerball"]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(true);

  useEffect(() => {
    getIngestStatus().then((s) => setActive(s.delivery_active)).catch(() => {});
  }, []);

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));

  const submit = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (!selected.length) {
      toast.error("Pick at least one game");
      return;
    }
    setLoading(true);
    try {
      const res = await subscribe({ email, games: selected });
      setDone(true);
      toast.success(res.message);
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-6" data-testid="alerts-card">
      <div className="flex items-center gap-2 mb-1">
        <Bell size={16} className="text-zinc-300" />
        <span className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-semibold">Draw Alerts</span>
      </div>
      <p className="text-sm text-zinc-400 mb-5">
        Get an email the moment new numbers land and fresh AI picks are ready.
      </p>

      {done ? (
        <div className="flex flex-col items-center justify-center py-6 text-center" data-testid="alerts-success">
          <div className="w-11 h-11 rounded-full bg-green-500/15 grid place-items-center mb-3">
            <Check size={20} className="text-green-400" />
          </div>
          <p className="text-sm text-zinc-200 font-semibold mb-1">You're subscribed!</p>
          <p className="text-xs text-zinc-500 max-w-xs">
            {active
              ? "We'll email you after each draw you follow."
              : "Email delivery activates once an email provider (AWS SES or Resend) is configured."}
          </p>
          <button onClick={() => { setDone(false); setEmail(""); }} data-testid="alerts-reset"
            className="mt-3 text-xs text-zinc-400 hover:text-zinc-100 underline transition-colors duration-200">
            Add another email
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="relative">
            <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
              placeholder="you@example.com" data-testid="alerts-email"
              className="bg-zinc-950 border-zinc-800 h-11 pl-9 text-sm" />
          </div>

          <div className="flex flex-wrap gap-2">
            {GAMES.map((g) => {
              const on = selected.includes(g.id);
              return (
                <button key={g.id} onClick={() => toggle(g.id)} data-testid={`alerts-toggle-${g.id}`}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200"
                  style={on
                    ? { backgroundColor: g.color, color: "#09090b", borderColor: g.color }
                    : { color: "#a1a1aa", borderColor: "#3f3f46" }}>
                  {g.label}
                </button>
              );
            })}
          </div>

          <Button onClick={submit} disabled={loading} data-testid="alerts-subscribe"
            className="w-full h-11 rounded-full bg-zinc-100 hover:bg-white text-zinc-900 font-semibold transition-colors duration-200">
            {loading ? "Subscribing…" : "Notify me"}
          </Button>

          {!active && (
            <div className="flex items-start gap-2 text-zinc-500" data-testid="alerts-inactive-note">
              <Info size={13} className="mt-0.5 shrink-0" />
              <p className="text-[11px]">
                Delivery is inactive until AWS SES or Resend is configured — but your subscription is saved.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
